<template>
    <div class="pay-in__body px-4 py-3">
        <payment-method-form
            :payments="form.payments"
            :isAmountCovered="isAmountFullyPaid"
            :showToPay="true"
            :amountReceivedDisabled="true"
            :remainingToPay="Math.max(0, amountDue - totalToPay)"
            :amountDue="amountDue"
            :allowEmpty="!!editPayment"
        />

        <hr class="pay-in__separator mb-3" />

        <!-- Notes -->
        <div class="mb-3">
            <label class="ml-2">Notes</label>
            <div class="pay-in__notes-wrapper">
                <input
                    type="text"
                    class="pay-in__notes-input"
                    v-model="form.notes"
                    :maxlength="NOTES_CHARACTER_LIMIT"
                />
                <div class="pay-in__notes-counter">
                    {{ form.notes.length || 0 }}/{{ NOTES_CHARACTER_LIMIT }}
                </div>
            </div>
        </div>

        <hr class="pay-in__separator mb-3" />

        <!-- Checkboxes -->
        <div class="mb-2">
            <div v-if="isNothingDue && showInvoice" class="pay-in__checkbox mb-1">
                <input
                    type="checkbox"
                    id="createInvoice"
                    v-model="form.createInvoice"
                >
                <label
                    for="createInvoice"
                    class="mb-0 pay-in__checkbox-label"
                >
                    Create invoice after saving
                </label>
            </div>

            <div class="pay-in__checkbox">
                <input
                    type="checkbox"
                    id="addBillingDetails"
                    v-model="form.addBillingDetails"
                >
                <label
                    for="addBillingDetails"
                    class="mb-0 pay-in__checkbox-label"
                >
                    Add billing details
                </label>
            </div>
        </div>

        <billing-details-form
            v-if="form.addBillingDetails"
            :billingDetails="form.billingDetails"
        />

        <!-- Due to Pay / Total -->
        <div class="d-flex justify-content-end align-items-center mt-5">
            <label class="mr-2">Due to Pay</label>
            <span class="fs-large font-weight-600 mr-4">
                {{ formatPrice(Math.max(0, amountDue - allocatedAmountReceived)) }} €
            </span>
            <label class="mr-2">Total</label>
            <span class="fs-large font-weight-600">
                {{ formatPrice(grandTotal) }} €
            </span>
        </div>
    </div>
</template>

<script>
import { reservationsMixin } from '@/mixins/reservations'
import BillingDetailsForm from '@/tools/modal/ReservationPayIn/BillingDetailsForm'
import PaymentMethodForm from '@/tools/modal/ReservationPayIn/PaymentMethodForm'

const NOTES_CHARACTER_LIMIT = 50

export default {
    mixins: [reservationsMixin],
    components: { BillingDetailsForm, PaymentMethodForm },
    props: {
        form: {
            type: Object,
            required: true,
        },
        grandTotal: {
            type: Number,
            required: true,
        },
        amountDue: {
            type: Number,
            required: true,
        },
        showInvoice: {
            type: Boolean,
            required: false
        },
        editPayment: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            NOTES_CHARACTER_LIMIT,
        }
    },
    computed: {
        totalToPay() {
            return this.form.payments.reduce((sum, payment) => sum + (parseFloat(payment.toPay) || 0), 0)
        },
        totalReceived() {
            return this.form.payments.reduce((sum, payment) => {
                const received = parseFloat(payment.amountReceived) || 0
                const toPay = parseFloat(payment.toPay) || 0
                return sum + Math.min(received, toPay)
            }, 0)
        },
        allocatedAmountReceived() {
            return this.form.payments.reduce((sum, payment) => {
                const received = parseFloat(payment.amountReceived) || 0
                const toPay = parseFloat(payment.toPay) || 0
                return sum + Math.min(received, toPay)
            }, 0)
        },
        isAmountFullyPaid() {
            return this.totalReceived >= this.amountDue && this.amountDue > 0
        },
        isNothingDue() {
            return !Math.max(0, this.amountDue - this.allocatedAmountReceived) > 0
        },
    },
    watch: {
        isNothingDue(value) {
            if (!value) this.form.createInvoice = false
        },
    },
}
</script>

<style lang="scss">
    .pay-in-modal .checkbox-component label {
        font-family: 'Open Sans Regular', Helvetica, Arial, sans-serif !important;
        font-weight: normal !important;
    }
</style>

<style lang="scss" scoped>
    @import '../../../assets/scss/_variables.scss';

    .pay-in__body {
        min-height: 11.25rem;
    }

    .pay-in__separator {
        border: 0;
        border-top: 1px solid $gray-light;
        margin: 0.25rem 0;
    }

    .pay-in__notes-wrapper {
        display: flex;
        flex-direction: column;
        width: 50%;
    }

    .pay-in__notes-input {
        width: 100%;
    }

    .pay-in__notes-counter {
        font-size: 0.75rem;
        color: #999;
        margin-top: 0.25rem;
        margin-left: auto;
        font-weight: 500;
    }

    .pay-in__checkbox {
        display: flex;
        align-items: center;
    }

    .pay-in__checkbox input[type='checkbox'] {
        appearance: none !important;
        -webkit-appearance: none !important;

        position: relative !important;

        width: 16px !important;
        height: 16px !important;
        min-width: 16px !important;
        max-width: 16px !important;

        margin: 0 8px 0 0 !important;
        padding: 0 !important;

        border: 1px solid #c9c9c9 !important;
        border-radius: 2px !important;

        background: #fff !important;

        cursor: pointer !important;

        box-sizing: border-box !important;
    }

    .pay-in__checkbox input[type='checkbox']:checked {
        background: $color-1 !important;
        border-color: $color-1 !important;
    }

    .pay-in__checkbox input[type='checkbox']:checked::after {
        content: '' !important;

        position: absolute !important;

        width: 7px !important;
        height: 4px !important;

        left: 3px !important;
        top: 3px !important;

        border-left: 2px solid #fff !important;
        border-bottom: 2px solid #fff !important;

        transform: rotate(-45deg) !important;

        box-sizing: border-box !important;
    }

    .pay-in__checkbox-label {
        font-family: 'Open Sans Regular', Helvetica, Arial, sans-serif !important;
        font-size: 14px !important;
        font-weight: normal !important;
        line-height: 16px !important;

        cursor: pointer !important;
    }
</style>


// WEBPACK FOOTER //
// Step2Details.vue?7ac01d2e